import { getLocalDateKey } from './reportDates';

function emptyBucket(key) {
  return {
    key,
    cashIn: 0,
    cashOut: 0,
    countIn: 0,
    countOut: 0,
    txnCount: 0,
  };
}

/** Transactions for a single calendar day (local dateKey on row). */
export function transactionsForDate(transactions, dateKey) {
  const dk = String(dateKey || '').slice(0, 10);
  return (transactions || []).filter((t) => String(t.dateKey || '').slice(0, 10) === dk);
}

/** Group by payment network (channel). */
export function aggregateByChannel(transactions, dateKey) {
  const list = transactionsForDate(transactions, dateKey);
  const map = new Map();
  for (const t of list) {
    const k = t.channel || '—';
    if (!map.has(k)) map.set(k, emptyBucket(k));
    const b = map.get(k);
    const amt = Number(t.amount) || 0;
    if (t.direction === 'in') {
      b.cashIn += amt;
      b.countIn += 1;
    } else {
      b.cashOut += amt;
      b.countOut += 1;
    }
    b.txnCount += 1;
  }
  return Array.from(map.values()).sort((a, b) => b.cashIn + b.cashOut - (a.cashIn + a.cashOut));
}

/** Group by wakala service type. */
export function aggregateByService(transactions, dateKey) {
  const list = transactionsForDate(transactions, dateKey);
  const map = new Map();
  for (const t of list) {
    const k = t.service || '—';
    if (!map.has(k)) map.set(k, emptyBucket(k));
    const b = map.get(k);
    const amt = Number(t.amount) || 0;
    if (t.direction === 'in') {
      b.cashIn += amt;
      b.countIn += 1;
    } else {
      b.cashOut += amt;
      b.countOut += 1;
    }
    b.txnCount += 1;
  }
  return Array.from(map.values()).sort((a, b) => b.txnCount - a.txnCount);
}

export function dayTotals(transactions, dateKey) {
  const list = transactionsForDate(transactions, dateKey);
  let cashIn = 0;
  let cashOut = 0;
  for (const t of list) {
    const amt = Number(t.amount) || 0;
    if (t.direction === 'in') cashIn += amt;
    else cashOut += amt;
  }
  return { cashIn, cashOut, count: list.length, netInMinusOut: cashIn - cashOut };
}

/** Last N local calendar days with totals (for trend strip). */
export function lastNDaysSeries(transactions, n = 7) {
  const keys = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date();
    d.setHours(12, 0, 0, 0);
    d.setDate(d.getDate() - i);
    keys.push(getLocalDateKey(d));
  }
  return keys.map((dateKey) => ({
    dateKey,
    ...dayTotals(transactions, dateKey),
  }));
}

/** Add net (in − out) to channel or service aggregate rows. */
export function withNetFlow(rows) {
  return (rows || []).map((r) => ({
    ...r,
    net: (Number(r.cashIn) || 0) - (Number(r.cashOut) || 0),
  }));
}

/**
 * Summarise last-N-days series for plain-English trend copy.
 * @param {Array<{ dateKey: string, cashIn: number, cashOut: number, count: number, netInMinusOut: number }>} series
 */
export function summarizeTrendSeries(series) {
  const s = Array.isArray(series) ? series : [];
  if (s.length === 0) {
    return {
      days: 0,
      totalIn: 0,
      totalOut: 0,
      net: 0,
      avgDailyNet: 0,
      bestDay: null,
      weakestDay: null,
      busiestDay: null,
    };
  }
  let totalIn = 0;
  let totalOut = 0;
  let bestDay = s[0];
  let weakestDay = s[0];
  let busiestDay = s[0];
  for (const d of s) {
    totalIn += Number(d.cashIn) || 0;
    totalOut += Number(d.cashOut) || 0;
    const net = Number(d.netInMinusOut) || 0;
    const bestNet = Number(bestDay.netInMinusOut) || 0;
    const weakNet = Number(weakestDay.netInMinusOut) || 0;
    if (net > bestNet) bestDay = d;
    if (net < weakNet) weakestDay = d;
    if ((d.count || 0) > (busiestDay.count || 0)) busiestDay = d;
  }
  const net = totalIn - totalOut;
  return {
    days: s.length,
    totalIn,
    totalOut,
    net,
    avgDailyNet: net / s.length,
    bestDay,
    weakestDay,
    busiestDay,
  };
}
